import React from 'react'
import { Box, styled } from '@mui/material'
import { useDesignSystem } from '@/design-system'

export type SettingsTab = 'profile' | 'content' | 'billing' | 'account' | 'privacy' | 'danger'

const Nav = styled(Box)({
  position: 'sticky',
  top: 76,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})

const NavButton = styled('button', {
  shouldForwardProp: (p) => p !== '$active' && p !== '$danger',
})<{ $active?: boolean; $danger?: boolean }>(({ $active, $danger }) => ({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  height: 38,
  padding: '0 12px',
  borderRadius: 9,
  fontSize: 13.5,
  fontWeight: $active ? 640 : 550,
  cursor: 'pointer',
  width: '100%',
  boxSizing: 'border-box',
  color: $active ? 'var(--ds-inverse-ink)' : $danger ? 'var(--ds-accent-deep-a)' : 'var(--ds-ink-muted)',
  background: $active ? ($danger ? 'var(--ds-accent-deep-a)' : 'var(--ds-ink)') : 'transparent',
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': $active
    ? undefined
    : {
        background: $danger ? 'color-mix(in srgb, var(--ds-accent-deep-a) 10%, transparent)' : 'var(--ds-hover)',
        color: $danger ? 'var(--ds-accent-deep-a)' : 'var(--ds-ink)',
      },
}))

const Dot = styled('span')({
  width: 7,
  height: 7,
  borderRadius: '50%',
  flexShrink: 0,
})

const Sep = styled(Box)({
  height: 1,
  background: 'var(--ds-border)',
  margin: '8px 4px',
})

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'content', label: 'Content & Storage' },
  { id: 'billing', label: 'Billing' },
  { id: 'account', label: 'Account & Security' },
  { id: 'privacy', label: 'Privacy' },
]

export interface SettingsNavProps {
  value: SettingsTab
  onChange: (tab: SettingsTab) => void
}

/** Left sub-nav for the settings page. */
export function SettingsNav({ value, onChange }: SettingsNavProps) {
  const { theme } = useDesignSystem()
  const dots = theme.accentDots

  return (
    <Nav>
      {TABS.map((tab, i) => (
        <NavButton key={tab.id} $active={value === tab.id} onClick={() => onChange(tab.id)}>
          <Dot style={{ background: dots[i % dots.length] }} />
          {tab.label}
        </NavButton>
      ))}
      <Sep />
      <NavButton $active={value === 'danger'} $danger onClick={() => onChange('danger')}>
        <Dot style={{ background: 'var(--ds-accent-deep-a)' }} />
        Danger Zone
      </NavButton>
    </Nav>
  )
}
